import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import Select from "../../../../../parts/inputs/select";
import {
  convertMeasurement,
  customRound,
  volumeUnits,
  weightUnits,
} from "../../../../../utils/nutrition-util";
import { simpleHash } from "../../../../../utils/string-helpers";

const DEFAULT_QUANTITY_NUMBER = 1;
const DEFAULT_QUANTITY_DESCRIPTION = "serving";

export const HandleFoodModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, mostUsedMealPlans, foodsByCoach } = locals;
  const allFoods = foodsByCoach || mostUsedMealPlans["foods"];

  const { foodId } = query;
  const currentFood = allFoods.find(({ _id }) => _id === foodId) || {
    name: "",
    brand: "",
    fullQuantity: "",
    quantityNumber: 0,
    quantityDescription: "",
    measurementsValue: 0,
    unitOfMeasure: "g",
    carbs: 0,
    proteins: 0,
    fats: 0,
    fibers: 0,
    calories: 0,
  };
  const {
    name: currentFoodName,
    brand: currentFoodBrand,
    quantityNumber: currentFoodQuantityNumber,
    quantityDescription: currentFoodQuantityDescription,
    measurementsValue: currentFoodMeasurementsValue,
    unitOfMeasure: currentFoodUnitOfMeasure,
    carbs: currentFoodCarbs,
    proteins: currentFoodProteins,
    fats: currentFoodFats,
    fibers: currentFoodFibers,
    calories: currentFoodCalories,
  } = currentFood;
  const title = foodId ? t("editMealPlan.handleFoodModal.titleEdit") : t("editMealPlan.handleFoodModal.titleAdd");
  const unitOptions = [...weightUnits, ...volumeUnits]; //grams, ounces, pounds, milliliters, teaspoon, tablespoon, fluid ounce and cup
  const unitOptionsToUse = !foodId
    ? unitOptions
    : weightUnits.includes(currentFoodUnitOfMeasure)
    ? weightUnits
    : volumeUnits;
  const regex = /^(\d+\/\d+|\d+\.\d+|\d+)\s+(.+)$/;
  const initialState = {
    name: currentFoodName || "",
    brand: currentFoodBrand || "",
    fullQuantity:
      currentFoodQuantityNumber && currentFoodQuantityDescription
        ? `${currentFoodQuantityNumber} ${currentFoodQuantityDescription}`
        : "",
    quantityNumber: currentFoodQuantityNumber || 0,
    quantityDescription: currentFoodQuantityDescription || "",
    measurementsValue: currentFoodMeasurementsValue || 0,
    unitOfMeasure: currentFoodUnitOfMeasure || "g",
    carbs: currentFoodCarbs || 0,
    proteins: currentFoodProteins || 0,
    fats: currentFoodFats || 0,
    fibers: currentFoodFibers || 0,
    calories: currentFoodCalories || 0,
  };
  const [formState, setFormState] = useState(initialState);
  const [errorQtyMessage, setErrorQtyMessage] = useState("");

  const {
    name,
    brand,
    fullQuantity,
    measurementsValue,
    unitOfMeasure,
    carbs,
    proteins,
    fats,
    fibers,
    calories,
    quantityNumber,
    quantityDescription,
  } = formState;

  /**
   * Function to send the information to the api
   */
  const handleSend = () => {
    const action = foodId ? `handle-food?foodId=${foodId}` : `handle-food`;

    const { convertedValue, convertedUnit } = convertMeasurement(
      measurementsValue,
      unitOfMeasure
    );

    const trimmedQty = String(formState.fullQuantity || "").trim();
    const useDefaultQty =
      !trimmedQty ||
      formState.quantityNumber === undefined ||
      !String(formState.quantityDescription || "").trim();

    const payload = {
      ...formState,
      ...(useDefaultQty
        ? {
            fullQuantity: `${DEFAULT_QUANTITY_NUMBER} ${DEFAULT_QUANTITY_DESCRIPTION}`,
            quantityNumber: DEFAULT_QUANTITY_NUMBER,
            quantityDescription: DEFAULT_QUANTITY_DESCRIPTION,
          }
        : {}),
      measurementsValue: convertedValue,
      unitOfMeasure: convertedUnit,
    };

    const requestId = simpleHash(JSON.stringify(payload));

    const bodyToSend = {
      ...payload,
      requestId,
    };

    submit(`/actions/meal-planner/${action}`, {
      method: "POST",
      body: bodyToSend,
    });
  };

  /**
   * Function to not allow "e" "+" and + "-" characters
   * @param {*} e
   */
  const handleKeyDown = (e) => {
    if (["e", "E", "-", "+"].includes(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Validate the quantity input and set quantityNumber and quantityDescription
   * @param {*} e
   */
  const handleQuantityInputChange = (e) => {
    const value = e.target.value;
    const match = value.trim().match(regex);

    if (match) {
      const [, number, description] = match;
      setFormState((prevState) => ({
        ...prevState,
        fullQuantity: value,
        quantityNumber: number,
        quantityDescription: description,
      }));
      setErrorQtyMessage("");
    } else {
      if (!value.trim()) {
        setErrorQtyMessage("");
      }
      setFormState((prevState) => ({
        ...prevState,
        fullQuantity: value,
        quantityNumber: undefined,
        quantityDescription: "",
      }));
    }
  };

  /**
   * Show an error message if the input value is invalid
   */
  const handleQuantityBlur = () => {
    const trimmed = String(formState.fullQuantity || "").trim();

    if (trimmed && !regex.test(trimmed)) {
      setErrorQtyMessage(t("editMealPlan.handleFoodModal.quantityError"));
    } else {
      setErrorQtyMessage("");
    }
  };

  /**
   * Select the unit of measure
   * @param {*} newVal
   * @param {*} name
   */
  const handleSelectChange = (newVal) => {
    setFormState((prevState) => ({
      ...prevState,
      unitOfMeasure: newVal,
    }));
  };

  /**
   * Save the inputs values
   * @param {*} e
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /**
   * Function to validate if the form is valid
   * @returns
   */
  const isFormValid = () => {
    const trimmedQty = String(fullQuantity || "").trim();
    const quantityOk =
      !trimmedQty ||
      (regex.test(trimmedQty) &&
        quantityNumber !== undefined &&
        String(quantityDescription || "").trim() !== "");

    return Boolean(
      name &&
        measurementsValue &&
        quantityOk &&
        !errorQtyMessage
    );
  };

  /**
   * Update the calories input everytime proteins, carbs or fats is affected
   */
  useEffect(() => {
    if (proteins === 0 && carbs === 0 && fats === 0) return;
    const proteinsKcal = customRound(proteins * 4);
    const carbsKcal = customRound(carbs * 4);
    const fatsKcal = customRound(fats * 9);
    const totalCalories = customRound(proteinsKcal + carbsKcal + fatsKcal);

    setFormState((prevState) => ({
      ...prevState,
      calories: totalCalories,
    }));
  }, [proteins, carbs, fats]);

  return (
    <Modal
      className="edit-food-modal"
      title={title}
      toClear={["foodId"]}
      backButton={true}
    >
      <div className="body">
        <div className="inputs">
          <div>
            <label className="label" htmlFor="name">
              {t("editMealPlan.handleFoodModal.foodName")}
            </label>
            <input
              type="text"
              placeholder={t("editMealPlan.handleFoodModal.foodNamePlaceholder")}
              name="name"
              value={name}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="label" htmlFor="brand">
              {t("editMealPlan.handleFoodModal.brandName")}
            </label>
            <input
              type="text"
              placeholder={t("editMealPlan.handleFoodModal.brandNamePlaceholder")}
              name="brand"
              value={brand}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="label" htmlFor="quantity">
              {t("editMealPlan.handleFoodModal.quantity")}
            </label>
            <input
              type="text"
              placeholder={t("editMealPlan.handleFoodModal.quantityPlaceholder")}
              name="quantity"
              value={fullQuantity}
              onChange={handleQuantityInputChange}
              onBlur={handleQuantityBlur}
            />
            {errorQtyMessage && (
              <span className="text-(--danger) mt-1 text-[0.6875rem]">
                {errorQtyMessage}
              </span>
            )}
          </div>
          <div>
            <label className="label" htmlFor="measurementsValue">
              {t("editMealPlan.handleFoodModal.measurements")}
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                placeholder={t("editMealPlan.handleFoodModal.measurementsPlaceholder")}
                name="measurementsValue"
                value={measurementsValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <Select
                name="unitOfMeasure"
                value={unitOfMeasure}
                onChange={(newVal) => handleSelectChange(newVal)}
                options={unitOptionsToUse.map((elem) => {
                  return { value: elem, text: elem };
                })}
              />
            </div>
          </div>
        </div>
        <div className="nutrition">
          <div>
            <label className="label protein" htmlFor="proteins">
              {t("editMealPlan.handleFoodModal.protein")}
            </label>
            <div>
              <input
                type="number"
                name="proteins"
                value={proteins}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label carbs" htmlFor="carbs">
              {t("editMealPlan.handleFoodModal.carbs")}
            </label>
            <div>
              <input
                name="carbs"
                type="number"
                value={carbs}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label fat" htmlFor="fats">
              {t("editMealPlan.handleFoodModal.fat")}
            </label>
            <div>
              <input
                type="number"
                name="fats"
                value={fats}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label text-[#10c268]" htmlFor="calories">
              {t("editMealPlan.handleFoodModal.kCal")}
            </label>
            <div>
              <input
                type="number"
                name="calories"
                value={calories}
                disabled={true}
              />
            </div>
          </div>
          <div>
            <label className="label text-[#10c268]" htmlFor="fibers">
              {t("editMealPlan.handleFoodModal.fiberOptional")}
            </label>
            <div>
              <input
                type="number"
                name="fibers"
                value={fibers}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
        </div>
        <div className="footer">
          <button
            data-button-type="primary"
            disabled={!isFormValid()}
            onClick={handleSend}
          >
            {t("editMealPlan.handleFoodModal.saveChanges")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

HandleFoodModal.propTypes = {
  locals: PropTypes.object,
};
